import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import { Box, Button, Chip, Grid, Popover, Stack } from '@mui/material';
import { authContext, Profile } from '@staffedupusa/shared-components';
import {
  ElementType,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import ApplicantAdditionalInfo from '@features/applicant/components/ApplicantAdditionalInfo/ApplicantAdditionalInfo';
import ApplicantReferencesList from '@features/applicant/components/ApplicantReferencesList/ApplicantReferencesList';
import { useGetApplicantReferences } from '@features/applicant/hooks/useGetApplicantReferences';
import { ApplicantReference } from '@features/applicationQuestions';
import ApplicationQuestionResponse from '@features/applicationQuestions/components/applicationQuestionResponse/ApplicationQuestionResponse';
import { Application } from '@features/applications';
import ApplicationHistoryTab from '@features/applications/components/ApplicationHistoryTab/ApplicationHistoryTab';
import { white } from '@styles/colors';
import DigitalOnboardingTab from '../DigitalOnboardingTab/DigitalOnboardingTab';
import { hasPermission } from '@features/profile/components/InviteUsersModal/PermissionUtils';
import { permissionsList } from '@features/profile/components/InviteUsersModal/constants';
import { selectedPlanName } from '@utils/pricing';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { track } from '@amplitude/analytics-browser';

type ApplicationViewTab =
  | 'questions'
  | 'resume'
  | 'additional info'
  | 'references'
  | 'onboarding'
  | 'history';

type SelectTabButton = {
  type: ApplicationViewTab;
  Icon: ElementType;
  label: string;
  disabled: boolean;
  count?: number;
};

type ApplicationViewTabsProps = {
  application: Application;
  defaultOnboarding?: Boolean;
  applicationDetails: Profile | null;
};

const ApplicationViewTabs: FC<ApplicationViewTabsProps> = ({
  application,
  defaultOnboarding,
  applicationDetails,
}) => {
  const { authData } = useContext(authContext);
  const { state } = useLocation();
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const { applicantReferencesData } = useGetApplicantReferences({
    applicantId: application?.applicant?.id,
  });

  const hasOnboardingPermission = !hasPermission(
    permissionsList.manageOnboarding,
    authData.user?.permissions?.permissions,
  );

  const selectTabButtons: SelectTabButton[] = [
    {
      label: 'Application Questions',
      type: 'questions',
      Icon: QuestionAnswerOutlinedIcon,
      disabled: !application.qualification_question_responses?.length,
    },
    {
      label: 'Uploaded Resume',
      type: 'resume',
      Icon: EditNoteIcon,
      disabled: !applicationDetails?.resume_path,
    },
    {
      label: 'Additional Resume Info',
      type: 'additional info',
      Icon: BusinessCenterOutlinedIcon,
      disabled:
        (!applicationDetails?.work_history ||
          applicationDetails?.work_history?.length < 1) &&
        (!applicationDetails?.education ||
          applicationDetails?.education?.length < 1) &&
        (!applicationDetails?.skills ||
          applicationDetails?.skills?.length < 1) &&
        (!applicationDetails?.certifications ||
          applicationDetails?.certifications?.length < 1),
    },
    {
      label: 'References',
      type: 'references',
      Icon: ThumbUpAltOutlinedIcon,
      disabled: !applicantReferencesData?.object?.length,
      count: applicantReferencesData?.object?.length ?? 0,
    },

    {
      label: 'Digital Onboarding',
      type: 'onboarding',
      Icon: ThumbUpAltOutlinedIcon,
      disabled: hasOnboardingPermission,
    },
    {
      label: 'Application History',
      type: 'history',
      Icon: HistoryOutlinedIcon,
      disabled: false,
    },
  ];

  const disabledTabs = selectTabButtons.filter((tab) => tab.disabled);
  const firstEnabledTab = selectTabButtons.find((tab) => !tab.disabled);
  const initialTabType = firstEnabledTab
    ? firstEnabledTab?.type
    : disabledTabs[0].type;

  const [selectedTab, setSelectedTab] =
    useState<ApplicationViewTab>(initialTabType);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredItem, setHoveredItem] = useState<ApplicationViewTab | ''>('');

  useEffect(() => {
    if (!defaultOnboarding) setSelectedTab(initialTabType);
  }, [applicantReferencesData]);

  useEffect(() => {
    if (state?.tab) {
      setSelectedTab(state?.tab);
    }
  }, [state]);

  useEffect(() => {
    if (defaultOnboarding) {
      setSelectedTab('onboarding');
    }
  }, []);

  const getText = () => {
    switch (hoveredItem) {
      case 'questions':
        return 'The applicant did not answer application questions';
      case 'resume':
        return 'The applicant did not upload a resume';
      case 'additional info':
        return 'This applicant did not provide additional resume information';
      case 'references':
        return 'This applicant did not provide pre-vetted references';
      case 'onboarding':
        return 'Your account permissions don’t allow access to onboarding. Reach out to your admin to update your account permissions';
      default:
        return 'Not provided';
    }
  };

  const renderPreviewFileModal = useMemo(() => {
    if (!applicationDetails?.resume_path) return null;

    const docUri = applicationDetails?.resume_path as string;
    const fileType = docUri.split('.').pop()?.toLowerCase();

    // Return only one instance of DocViewer
    return (
      <DocViewer
        prefetchMethod="GET"
        documents={[{ uri: docUri, fileType }]}
        pluginRenderers={DocViewerRenderers}
        config={{
          header: { disableHeader: true },
          renderMode: 'canvas', // Ensure it's using canvas for rendering
          renderTextLayer: false, // Disable the text layer
          renderAnnotationLayer: false, // Disable the annotation layer
        }}
      />
    );
  }, [applicationDetails?.resume_path]);

  const getTab = () => {
    switch (selectedTab) {
      case 'questions':
        return application?.qualification_question_responses?.length ? (
          <Stack
            spacing={3}
            bgcolor={white}
            p={4}
            borderRadius={8}
            height="100%"
          >
            {application?.qualification_question_responses.map((response) => (
              <ApplicationQuestionResponse
                key={response.question}
                response={response}
              />
            ))}
          </Stack>
        ) : null;
      case 'resume':
        return (
          <Box
            bgcolor={white}
            p={4}
            borderRadius={8}
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {renderPreviewFileModal}
          </Box>
        );
      case 'additional info':
        return (
          <Box
            bgcolor={white}
            p={4}
            borderRadius={8}
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <ApplicantAdditionalInfo
              applicationDetails={applicationDetails as Profile}
            />
          </Box>
        );
      case 'references':
        return (
          <Box bgcolor={white} p={4} borderRadius={8} height="100%">
            <ApplicantReferencesList
              references={
                applicantReferencesData?.object as ApplicantReference[]
              }
            />
          </Box>
        );
      case 'onboarding':
        return <DigitalOnboardingTab application={application} />;
      case 'history':
        return <ApplicationHistoryTab application={application} />;
      default:
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={5} md={3}>
        <Stack spacing={2}>
          {selectTabButtons.map(({ Icon, label, type, disabled, count }) => {
            const isSelected = type === selectedTab;

            return (
              <Box key={label}>
                <Box
                  sx={{ position: 'relative' }}
                  onMouseEnter={(e) => {
                    if (disabled) {
                      setAnchorEl(e.currentTarget);
                      setHoveredItem(type);
                    }
                  }}
                  onMouseLeave={() => {
                    setAnchorEl(null);
                    setHoveredItem('');
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon />}
                    onClick={() => {
                      if (type === 'onboarding' && isCompany) {
                        track(
                          `${amplitudeEventsName.APPLICANTS_CLICK_DIGITAL_ONBOARDING}-${selectedPlan}`,
                        );
                      }
                      setSelectedTab(type);
                    }}
                    disabled={disabled}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      bgcolor: white,
                      color: 'secondary.main',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      fontSize: '14px',
                      borderColor:
                        isSelected && !disabled
                          ? 'secondary.main'
                          : 'transparent',
                      '&:hover': {
                        bgcolor: white,
                        borderColor: 'secondary.main',
                      },
                    }}
                    id={`button-${label.toLowerCase().replace(/\s+/g, '-') }`}
                  >
                    {label}
                    {Boolean(count) && (
                      <Chip
                        label={count}
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Box>
                <Popover
                  open={Boolean(anchorEl) && hoveredItem === type}
                  anchorEl={anchorEl}
                  sx={{
                    pointerEvents: 'none',
                    borderRadius: 8,
                    fontSize: '14px',
                  }}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  onClose={() => {
                    setAnchorEl(null);
                    setHoveredItem('');
                  }}
                  slotProps={{
                    paper: {
                      style: {
                        boxShadow: '0 1px 12px rgba(0,0,0,0.15)',
                        padding: 15,
                        paddingTop: '7px',
                        paddingBottom: '7px',
                        borderRadius: 8,
                      },
                    },
                  }}
                  disableRestoreFocus
                >
                  {getText()}
                </Popover>
              </Box>
            );
          })}
        </Stack>
      </Grid>
      <Grid item xs={12} sm={7} md={9}>
        {getTab()}
      </Grid>
    </Grid>
  );
};

export default ApplicationViewTabs;
